import React from "react"
import { money, signedMoney, pct } from "./format"

// MRR movement composition. Avoids a zero-baselined waterfall (which squashes
// small deltas against a large base): instead a Starting → Current header, a
// gain-vs-loss split bar, and per-driver bars scaled to the largest *movement*
// so each driver stays legible regardless of the absolute MRR.
export function MrrMovementPanel({ movement, copy }) {
  const cur = movement.currency
  const gained = movement.gained_cents || 0
  const lost = movement.lost_cents || 0
  const swing = gained + lost || 1
  const upTone = movement.net_cents >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"

  const drivers = movement.drivers || []
  const maxDriver = Math.max(1, ...drivers.map((d) => Math.abs(d.cents)))

  return (
    <div className="space-y-4">
      {/* Net revenue retention badge */}
      <div className="flex items-center justify-between rounded-lg bg-indigo-50 dark:bg-indigo-950/40 px-4 py-2">
        <span className="text-xs font-medium uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
          {copy.movement.nrr}
        </span>
        <span className="text-sm font-bold text-indigo-700 dark:text-indigo-300">{pct(movement.nrr_pct)}</span>
      </div>

      {/* Starting → Current → Net change */}
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 px-4 py-3">
        <div className="flex items-end gap-3">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{copy.movement.starting}</p>
            <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">{money(movement.starting_cents, cur)}</p>
          </div>
          <span className="pb-1 text-gray-400 dark:text-gray-500">→</span>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{copy.movement.current}</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{money(movement.current_cents, cur)}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400">{copy.movement.netChange}</p>
          <p className={`text-lg font-bold ${upTone}`}>
            {signedMoney(movement.net_cents, cur)} <span className="text-sm">({pct(movement.net_pct)})</span>
          </p>
        </div>
      </div>

      {/* Gain vs loss split bar */}
      <div>
        <div className="flex h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
          <div className="bg-emerald-500" style={{ width: `${(gained / swing) * 100}%` }} />
          <div className="bg-red-500" style={{ width: `${(lost / swing) * 100}%` }} />
        </div>
        <div className="mt-1 flex justify-between text-xs">
          <span className="text-emerald-600 dark:text-emerald-400">+{money(gained, cur)} {copy.movement.gained}</span>
          <span className="text-red-600 dark:text-red-400">-{money(lost, cur)} {copy.movement.lost}</span>
        </div>
      </div>

      {/* Per-driver bars */}
      <ul className="space-y-2">
        {drivers.map((d) => {
          const positive = d.cents >= 0
          const width = (Math.abs(d.cents) / maxDriver) * 100
          return (
            <li key={d.kind} className="grid grid-cols-[6rem_1fr_auto] items-center gap-3 text-sm">
              <span className="text-gray-600 dark:text-gray-400">{copy.movement.drivers[d.kind] || d.kind}</span>
              <span className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <span
                  className={`block h-full rounded-full ${positive ? "bg-emerald-500" : "bg-red-500"}`}
                  style={{ width: `${width}%` }}
                />
              </span>
              <span className={positive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}>
                {signedMoney(d.cents, cur)}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
